import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../api';
import AIIntelligentAssistant from '../components/AIIntelligentAssistant.js';

const THEME = {
    primary: '#C0722C',
    danger: '#D32F2F',
    success: '#2E7D32',
    warning: '#ED6C02'
};

const ICONS = {
    health: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>,
    users: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    threat: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
    latency: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polyline></svg>,
    alert: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        activeUsers: 0,
        recentThreats: 0,
        uptime: 0,
        apiStatus: 'LOADING'
    });
    const [advisoryHidden, setAdvisoryHidden] = useState(sessionStorage.getItem('securityAdvisoryInvestigated') === 'true');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(`${API_BASE_URL}/api/dashboard/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(data);
            } catch (err) {
                console.error("Failed to load dashboard stats", err);
            }
        };

        fetchStats();
        const interval = setInterval(fetchStats, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleInvestigate = () => {
        sessionStorage.setItem('securityAdvisoryInvestigated', 'true');
        setAdvisoryHidden(true);
        navigate('/admin/audit');
    };

    const Widget = ({ title, value, color, icon, detail }) => (
        <div style={{
            backgroundColor: '#FFFFFF',
            padding: '30px',
            borderRadius: '24px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
            flex: 1,
            minWidth: '260px',
            border: '1px solid #F0E8DB',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.3s ease',
            cursor: 'default'
        }}>
            <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                backgroundColor: `${color}08`,
                borderRadius: '50%'
            }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', position: 'relative' }}>
                <span style={{ color: '#999', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>{title}</span>
                <span style={{ color: color, display: 'flex' }}>{icon}</span>
            </div>
            <div style={{
                fontSize: '32px',
                fontWeight: '800',
                color: THEME.primary,
                marginBottom: '8px',
                position: 'relative',
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '-0.5px'
            }}>
                {value}
            </div>
            <div style={{ fontSize: '14px', color: '#777', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '6px', height: '6px', backgroundColor: color, borderRadius: '50%' }}></span>
                {detail}
            </div>
        </div>
    );

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ marginBottom: '40px' }}>
                <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#2C1A10' }}>System Intelligence</h1>
                <p style={{ margin: 0, color: '#999', fontSize: '15px' }}>Real-time oversight of Vidhan Sabha administrative infrastructure.</p>
            </div>

            {/* Health Widgets */}
            <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap', marginBottom: '50px' }}>
                <Widget
                    title="System Status"
                    value={stats.apiStatus}
                    color={stats.apiStatus === 'HEALTHY' ? THEME.success : THEME.danger}
                    icon={ICONS.health}
                    detail={`Production Uptime: ${(stats.uptime / 3600).toFixed(1)}h`}
                />
                <Widget
                    title="Identity Pulse"
                    value={stats.activeUsers}
                    color={THEME.primary}
                    icon={ICONS.users}
                    detail="Active Session Concurrency"
                />
                <Widget
                    title="Security Perimeter"
                    value={stats.recentThreats}
                    color={stats.recentThreats > 0 ? THEME.danger : THEME.success}
                    icon={ICONS.threat}
                    detail="Auth Anomalies (24h)"
                />
                <Widget
                    title="Network Latency"
                    value="12ms"
                    color={THEME.success}
                    icon={ICONS.latency}
                    detail="Optimal Backbone Speed"
                />
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '40px' }}>
                <button
                    onClick={() => navigate('/admin/sessions')}
                    style={{
                        padding: '20px',
                        backgroundColor: '#fff',
                        border: '1px solid #eee',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                        flex: 1
                    }}
                >
                    <div style={{ padding: '10px', backgroundColor: '#FFF3E0', borderRadius: '10px', color: '#E65100' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    </div>
                    <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: '700', color: '#2C1A10', fontSize: '16px' }}>Manage Sessions</div>
                        <div style={{ color: '#888', fontSize: '13px' }}>Create & Configure Legislative Sessions</div>
                    </div>
                </button>
            </div>

            {/* Security Alerts - Masterpiece Style */}
            {stats.recentThreats > 0 && !advisoryHidden && (
                <div style={{
                    background: 'linear-gradient(135deg, #FFF5F5 0%, #FFF 100%)',
                    border: '1.5px solid #FFEBEE',
                    borderRadius: '24px',
                    padding: '35px',
                    marginBottom: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '30px',
                    boxShadow: '0 15px 40px rgba(229, 57, 53, 0.08)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '6px',
                        backgroundColor: '#E53935'
                    }}></div>
                    <div style={{
                        color: '#E53935',
                        backgroundColor: '#FFEBEE',
                        padding: '15px',
                        borderRadius: '16px',
                        display: 'flex'
                    }}>
                        {ICONS.alert}
                    </div>
                    <div>
                        <h3 style={{ margin: '0 0 6px 0', color: '#2C1A10', fontSize: '20px', fontWeight: '800' }}>Critical Security Advisory</h3>
                        <p style={{ margin: 0, color: '#666', fontSize: '15px', lineHeight: '1.6' }}>
                            Our perimeter sensors detected <strong>{stats.recentThreats}</strong> unauthorized access attempts.
                            The system is under elevated surveillance. Immediate review of audit logs is mandatory.
                        </p>
                    </div>
                    <button style={{
                        marginLeft: 'auto',
                        padding: '14px 28px',
                        backgroundColor: '#E53935',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontWeight: '700',
                        fontSize: '14px',
                        boxShadow: '0 8px 20px rgba(229, 57, 53, 0.3)',
                        transition: 'all 0.2s'
                    }} onClick={handleInvestigate}>
                        Investigate Now
                    </button>
                </div>
            )}

            {/* Governance Master Protocol - Redesigned Tabular View */}
            <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '30px',
                padding: '40px',
                boxShadow: '0 15px 45px rgba(0,0,0,0.04)',
                border: '1px solid #F0E8DB',
                position: 'relative'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '35px' }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#FDF8F3',
                        borderRadius: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: THEME.primary,
                        boxShadow: '0 8px 15px rgba(192, 114, 44, 0.08)'
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <div>
                        <h3 style={{ margin: 0, color: '#2C1A10', fontSize: '22px', fontWeight: '800' }}>Governance Master Protocol</h3>
                        <p style={{ margin: 0, color: '#999', fontSize: '13px', fontWeight: '500' }}>Operational mandates for high-level administrative oversight.</p>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {[
                        { title: 'Information Perimeter', desc: 'Read-only access to legislative floor activities.', protocol: 'ENFORCED' },
                        { title: 'Immutable Dossier', desc: 'Global logging of all administrative directives.', protocol: 'ACTIVE' },
                        { title: 'Authentication Hardening', desc: 'Secure 2FA required for session initialization.', protocol: 'MANDATORY' },
                        { title: 'Authority Delegation', desc: 'Elevated role assignment requires dual-stage confirmation.', protocol: 'RESTRICTED' }
                    ].map((item, idx) => (
                        <div key={idx} style={{
                            padding: '25px',
                            backgroundColor: idx % 2 === 0 ? '#FDF8F3' : '#FFFFFF',
                            borderRadius: '20px',
                            border: '1px solid #F0E8DB',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.3s'
                        }}>
                            <div>
                                <h4 style={{ margin: '0 0 5px 0', fontSize: '15px', fontWeight: '800', color: '#2C1A10' }}>{item.title}</h4>
                                <p style={{ margin: 0, fontSize: '12px', color: '#777', lineHeight: '1.4' }}>{item.desc}</p>
                            </div>
                            <div style={{
                                padding: '6px 12px',
                                backgroundColor: '#2C1A10',
                                color: '#EAD7BB',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '900',
                                letterSpacing: '1px'
                            }}>
                                {item.protocol}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* AI Assistant Integration */}
            <AIIntelligentAssistant />
        </div>
    );
};

export default AdminDashboard;
